import { usePathname } from 'next/navigation'

import { useCartDialog, useCartInterstitialDialog } from '../../app/_providers/CartProvider.client'
import { useFeatureFlag } from '../../app/_providers/ExperimentationProvider.client'
import { useTranslation } from '../../app/_providers/TranslationProvider.client'
import { CartContainer, UseCartNewCartItem } from '../../brunswick/containers/cart'
import { MultiAddToBagService } from '../../brunswick/containers/multi-add-to-bag'
import { KitEmbroideryService } from '../../containers/EmbroideryService'
import { Variant } from '../../types/graphql'

const INTERSTITIAL_PATH_PATTERN = /\/(products|kits)\/.+/

export function useBagMultiple({
  discountPrice,
  hasItemWithMissingSize,
  isKitFinalSale,
  kitId,
  kitName,
  kitSku,
  price,
  swPromoEligible,
  variants,
}: {
  discountPrice?: number | undefined | null
  hasItemWithMissingSize: boolean
  isKitFinalSale: boolean
  kitId: string
  kitName: string
  kitSku: string
  price: number
  swPromoEligible: boolean | undefined
  variants: ReadonlyArray<Variant> | null | undefined
}) {
  const { addItems, cart, errorMessage: cartErrorMessage } = CartContainer.useContainer()
  const { open: openMiniCartDialog } = useCartDialog()
  const { open: openCartInterstitialDialog } = useCartInterstitialDialog()
  const kitEmbroideryService = KitEmbroideryService.useContainer()
  const multiAddToBagService = MultiAddToBagService.useContainer()
  const pathname = usePathname()
  const {
    cart: { finalSale, nonReturnable },
  } = useTranslation()

  const isValidPathForInterstitial = pathname?.search(INTERSTITIAL_PATH_PATTERN) !== -1

  const cartInterstitialTestDecision = useFeatureFlag<
    'no-cart-interstitial' | 'yes-cart-interstitial'
  >({
    key: 'cart-interstitial-test',
    controlVariant: 'no-cart-interstitial',
    ifAccessedPriorToDecisionInitialization: 'return-null-while-pending',
    skip: !isValidPathForInterstitial,
  })

  const shouldDisplayCartInterstitial = cartInterstitialTestDecision === 'yes-cart-interstitial'

  //This property is used as a visible (no underscore prefix) line item on the minicart and checkout page
  const finalSaleProperty = {
    [finalSale]: nonReturnable,
  }

  // If a kit is discounted, pass a line item property to each item, with the same discount percentage.
  // The `set-discount` function will apply this discount to each item.
  const discountPct = 100 - ((discountPrice ?? price) / price) * 100
  const validDiscountPct = !isNaN(discountPct) && discountPct > 0 && discountPct <= 100
  const discountProperty =
    discountPrice && validDiscountPct ? { _kitDiscountPct: `${discountPct}` } : {}

  function handleAddToBagClick() {
    if (hasItemWithMissingSize) {
      multiAddToBagService.setAddToBagErrors({ missingRequiredSize: true })
      /**
       * We must wait for the React state update/re-render before [data-is-invalid=true] will
       * be applied to the invalid size section title element. So we schedule the following
       * logic to be run in the next iteration of the event loop:
       */
      setTimeout(() => {
        const sizeSections = document.getElementsByClassName('size-section-title')
        const elementWithError = Array.from(sizeSections).find(
          el => el.getAttribute('data-has-error') === 'true'
        ) as HTMLElement | undefined
        if (elementWithError) {
          try {
            elementWithError.scrollIntoView({ behavior: 'smooth', block: 'start' })
            elementWithError.focus({ preventScroll: true })
          } catch {}
        }
      }, 0)
    } else {
      addToBag()
    }
  }

  function resetInvalidBagClickStatus(): void {
    multiAddToBagService.setAddToBagErrors({ missingRequiredSize: false })
  }

  async function addToBag() {
    if (!variants || variants.length < 1) return
    multiAddToBagService.setIsAddToCartInFlight(true)
    const _swPromoEligible: string = swPromoEligible ? 'true' : 'false'

    const kitProperties = {
      _kit: kitName,
      _kitSku: kitSku,
      _kitId: kitId,
      _swPromoEligible,
    }

    const itemsToAdd: UseCartNewCartItem[] = variants
      .map(variant => {
        const variantGid: string = variant.shopifyId
        const embroideryOptions = kitEmbroideryService.getEmbroideryCartOption(variantGid)
        const finalSale = variant?.product?.finalSale || isKitFinalSale

        if (embroideryOptions && embroideryOptions.valid) {
          const items: UseCartNewCartItem[] = [
            {
              finalSale: true,
              item: embroideryOptions.embroideryItemVariant,
              properties: {
                _kitId: kitId,
                _swPromoEligible,
                ...embroideryOptions.embroideryLineItemProperties,
                ...finalSaleProperty,
              },
            },
            {
              finalSale: true,
              item: variant,
              properties: {
                ...kitProperties,
                ...discountProperty,
                ...finalSaleProperty,
                _embroideryId: variantGid,
                _guid: embroideryOptions.embroideryLineItemProperties._guid,
                _LPROP: embroideryOptions.embroideryLineItemProperties._LPROP,
                _logo_id: embroideryOptions.embroideryLineItemProperties._logo_id,
              },
            },
          ]
          let logoEmbroiderySetupInCart = false
          for (let key in cart?.items) {
            if (
              cart?.items[key]?.properties?._logo_setup_embroideryId ===
              embroideryOptions.embroideryLineItemProperties._logo_setup_embroideryId
            ) {
              logoEmbroiderySetupInCart = true
              break
            }
          }

          if (!logoEmbroiderySetupInCart && embroideryOptions.embroiderySetupVariant) {
            items.push({
              finalSale: true,
              item: embroideryOptions.embroiderySetupVariant,
              properties: {
                ...kitProperties,
                ...(embroideryOptions.embroideryLineItemProperties._logo_setup_embroideryId && {
                  _embroideryId:
                    embroideryOptions.embroideryLineItemProperties._logo_setup_embroideryId,
                }),
                ...(embroideryOptions.embroideryLineItemProperties._logo_setup_guid && {
                  _guid: embroideryOptions.embroideryLineItemProperties._logo_setup_guid,
                }),
                _is_logo_setup: 'true',
                _logo_id: embroideryOptions.embroideryLineItemProperties._logo_id,
              },
            })
          }
          return items
        } else {
          return {
            finalSale,
            item: variant,
            properties: {
              ...kitProperties,
              ...discountProperty,
              ...(finalSale ? finalSaleProperty : {}),
            },
            swPromoEligible,
          }
        }
      })
      .flat()

    await addItems(itemsToAdd)

    multiAddToBagService.setIsAddToCartInFlight(false)

    // we only want to open the interstitial or minicart if no errors occurred on `addItems`
    if (!cartErrorMessage) {
      if (shouldDisplayCartInterstitial) {
        openCartInterstitialDialog()
      } else {
        openMiniCartDialog()
      }
    }
  }

  return {
    handleAddToBagClick,
    resetInvalidBagClickStatus,
  }
}
